import React from 'react'
import PubSub from 'pubsub-js'
import PropTypes from 'prop-types'
import { Button, Card, CardBody, CardHeader } from 'reactstrap'
import apiBarcode from '../../services/api/apiBarcode'
import moment from 'moment'
import printImage from "../helpers/printImage"
import getOrderLocationType from "../helpers/getOrderLocationType"
import { pushNewItemHistory } from "../../services/api/apiItemHistory"
import ItemPackageStatus from "./ItemPackageStatus"
import { message } from 'antd'
import { buildLabelSrc } from './labelStatus'

class ShipOrderDetails extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            isLoadingShipping: false,
            isLoadingNote: false,
            note: '',
            error: [],
        }
    }

    componentDidMount() {
        this._loadStateFromProps()
        this._setupKeyup()
        this._printLabel()
    }

    componentWillUnmount() {
        document.removeEventListener("keyup", this._onKeyup)
    }

    _isPrinted = (item) => {
        // Quy trình FBA: các item chạy độc lập đến khâu ship, không bắt buộc tất cả product completed
        if (item.order_type === 'FBA') return true
        const { products } = item
        return !products.find(product => product.status !== 'completed' && product.status !== "cancelled")
    }

    _printLabel = async () => {
        const {item, onLoadingPrint} = this.props
        if (!this._isPrinted(item)) return

        const { labelDetails, first_barcode_in_batch_ship, first_batch_ship_box, tags = [] } = item
        const hasCombineShipTag = tags.some(item => /^combine-ship(-\w+)?$/.test(item))
        const canPrint = this.props.ableToPrint()

        // FAC-23754: src được tính tập trung qua buildLabelSrc (dùng chung với LabelStatusPanel)
        // để preview và label in thực tế luôn khớp nhau. Behavior in (gate canPrint, message lỗi,
        // SB_FAST in kể cả khi !canPrint) được giữ nguyên bên dưới.
        const src = await buildLabelSrc(item)

        // SB_FAST — giữ nguyên @FAC-22792: in kể cả khi !canPrint nếu KHÔNG phải gộp ship,
        // còn gộp ship thì chỉ in khi canPrint. (carrier nằm trên first_barcode_in_batch_ship)
        const topCarrier = labelDetails && labelDetails.shipping_carrier
        const isLLCorIB = ['flashship_usps', 'flashship_usps_manual', 'GC_LLC', 'ib_blue'].includes(topCarrier)
        const fbCarrier = first_barcode_in_batch_ship && first_batch_ship_box
            && first_barcode_in_batch_ship.labelDetails
            && first_barcode_in_batch_ship.labelDetails.shipping_carrier
        if (!isLLCorIB && fbCarrier === 'SB_FAST' && src) {
            const doPrint = !!(!hasCombineShipTag || (hasCombineShipTag && canPrint))
            if (doPrint) printImage(src)
            return
        }

        // Các loại cần tracking nhưng chưa có → giữ nguyên thông báo lỗi cũ
        if (!src) {
            const { shipping_carrier, partnerTrackingNumber } = { ...labelDetails }
            if (['flashship_usps', 'flashship_usps_manual', 'GC_LLC'].includes(shipping_carrier) && !partnerTrackingNumber) {
                message.error('Đơn chưa được gen label trên hệ thống LLC')
            } else if (shipping_carrier === 'ib_blue' && !partnerTrackingNumber) {
                message.error('Item không tồn tại tracking number.')
            }
            return
        }

        !!canPrint && printImage(src, onLoadingPrint)
    }

    _onKeyup = (e) => {
        if (e.key === 'Enter') {
            this._handleClickShip().then()
        }
    }

    _setupKeyup = () => {
        document.addEventListener("keyup", this._onKeyup)
    }

    _loadStateFromProps = () => {
        const { item } = this.props
        const { note } = item
        this.setState({ note })
    }

    _renderIconShip = () => {
        if (this.state.isLoadingShipping)
            return <i className="icon-spinner2 spinner  ml-2" />
        return <i className="icon-truck ml-2" />
    }

    _handleClickShip = async () => {
        this.setState({
            isLoadingShipping: true,
            error: [],
        })

        try {
            const { item, reloadItem, barcodeNumber, barcodeNumbers, clearSavedState } = this.props
            const trimmedBarcode = barcodeNumber.replace('#', '')

            for (let product of item.products) {
                if (product.status !== 'completed' && product.status !== 'cancelled') throw new Error('Có sản phẩm chưa được in.')
            }

            const payload = {}
            if(item && item.siblingsBarcodes) payload.siblingsBarcodes = barcodeNumbers.filter(item => item !== barcodeNumber).map(item => item.replace('#', ''))

            const resUpdated = await apiBarcode.markLabelled(trimmedBarcode, payload)
            const { success, data, message } = resUpdated.data

            if (!success) throw new Error(message)

            if (data.hasOwnProperty('errorItems')) {
                return this.setState({
                    error: data.errorItems,
                    isLoadingShipping: false,
                })
            }

            setTimeout(async () => {
                const { item: _item } = this.props
                const { labelDetails, barcodeNumber: _barcodeNumber } = _item
                const { partnerTrackingNumber } = Object.assign({}, labelDetails)

                return pushNewItemHistory(_barcodeNumber + '_1', {
                    event_key: 'mark_as_shipped',
                    event_description: "Mark package as shipped.",
                    event_meta: {
                        partnerTrackingNumber,
                    },
                })
            }, 0)

            await reloadItem()

            await clearSavedState()
            PubSub.publish('FOCUS_INPUT_SEARCH')
        } catch (e) {
            alert(e.message || 'Lỗi không xác định')
        } finally {
            this.setState({ isLoadingShipping: false })
        }
    }

    _handleCancel = () => {
        const r = window.confirm('Bạn có chắc chắn muốn hủy?')
        if (r) PubSub.publish('FOCUS_INPUT_SEARCH')
    }

    _handleChangeInput = (e) => {
        const { target: { name, value } } = e
        this.setState({ [name]: value })
    }

    _renderIconNote = () => {
        if (this.state.isLoadingNote)
            return <i className="icon-spinner2 spinner ml-2" />
        return <i className="icon-quill4 ml-2" />
    }

    _handleSaveNote = async () => {
        this.setState({ isLoadingNote: true })

        try {
            const { item } = this.props
            const { note } = this.state
            const resUpdated = await apiBarcode.updateBarcode(item._id, { note })

            const { success, message } = resUpdated.data
            if (!success) throw new Error(message)
        } catch (e) {
            alert(e.message)
        }

        this.setState({ isLoadingNote: false })
    }

    _handleLoadedTrackingStatus = async () => {

    }

    _checkTypeProducts = (products) => {
        const types = [
            'MASK_3D',
            'MASK_HYPER_3D',
            'MASK_SQUARE_3D',
            'MASKCC_3D',
            'MASKCCNUT_3D',
            'ORNAMENT_3D',
            'STICKER',
            'MASKCC_V2_3D',
            'MASK_PS_3D',
            'MASK_PQ_3D',
            'MASK_NEP_3D',
            'CUSTOM_ORNAMENT_3D',
            'CUT_ORNAMENT_3D',
            'ENGRAVED_ORNAMENT_3D',
            '1_MDF_ORNAMENT_3D',
            '2_MDF_ORNAMENT_3D',
            '1_WHITE_ORNAMENT_DZT',
            '1_WHITE_PLASTIC_ORNAMENT',
            '1_WHITE_PLASTIC_CAR_ORNAMENT',
            'MDF_CUSTOM_ORNAMENT_3D',
            'MICA_KEYCHAIN_3D',
            'PET_KEYCHAIN_3D',
            'CUSTOM_ORNAMENT_V2_3D',
            'MASK_NUT_3D',
            'MASK_NEP_NUT_3D',
            'MICA_PIN',
            'BRAND_KIT',
            'MICA_ORNAMENT_UID',
            '1L_WOOD_ORNAMENT_UID',
            '1_ALUMINIUM_ORNAMENT',
            '2_ALUMINIUM_ORNAMENT',
            '1_CERAMIC_ORNAMENT',
            '1_CERAMIC_ORNAMENT_DZT',
            '2_CERAMIC_ORNAMENT',
            'GLASS_ORNAMENT',
            'ORNAMENT_IN3',
            'CUSTOM_ORNAMENT_V2_IN3',
            'SHAKER_ORNAMENT',
            '1_ALUMINIUM_ORNAMENT_DZT',
            'WOOD_ACRYLIC_ORNAMENT_DZT',
            'CUS_ORNAMENT_V2_REDBOX',
            '2_CERAMIC_ORNAMENT_PAW',
            '1L_WOOD_ORNAMENT_PAW',
            '2L_WOOD_ORNAMENT_PAW',
            '1_WHITE_ORNAMENT_PAW',
            '2_ALU_ORNAMENT_PAW',
            'HEART_KEYCHAIN_SET',
            'MASK_01',
            'MASK_SQUARE_01',
            'MASKCC_01',
            'CUT_ORN_0203CU',
            'MASKCC_V2_01',
            'CUSTOM_ORN_0103MTM',
            'CUT_ORN_0203CU',
            '1W_ORN_0103MT',
            '1W_ORN_0103MT',
            '1W_ORN_0103MT',
            '2L_KEY_0102MT',
            'CUSTOM_ORN_0103MTM',
            'CUSTOM_ORN_0103MTM',
            '1L_ORN_0203TM',
            '1S_ALU_ORN_0500D7TM',
            '2S_ALU_ORN_0500D7TM',
            '1S_CER_ORN_0303TM',
            '1S_CER_ORN_0303TM',
            '2S_CER_ORN_0303TM',
            'GLASS_ORN_0406MT',
            'CUSTOM_ORN_0103IN3',
            'CUSTOM_ORN_0103IN3',
            '3L_SHAKER_0102MTM',
            '1S_ALU_ORN_0500D7TM',
            '2L_REG_ORN_12MT',
            '2L_SUN_ORN_12MM',
            '2L_SUN_ORN_DZT_12MM',
            'CUSTOM_ORN_0103MTM',
            '2S_CER_ORN_0303TM',
            '1L_ORN_0203TM',
            '2L_ORN_0203TM',
            '1W_ORN_0103MT',
            '1W_ORN_DZT_0103MT',
            '2S_ALU_ORN_0500D7TM',
            '2P_HEART_KEY_0502KH',
            '2S_ORN_0203TM',
            'GIFTBOX_0700NO',
            'UV_LED_BASE_0200',
            'WOOD_HANGER_48',
            'GIFTCARD_1800NO',
            'BOX_0700NO',
        ]

        return products.filter(item => {
            return item && (types.includes(item.type) || item.status === 'cancelled')
        })
    }

    render() {
        const { note, isLoadingNote, isLoadingShipping, error } = this.state
        const canPrint = this.props.ableToPrint()
        const { item, total, current } = this.props
        const { first_barcode_in_batch_ship, tags = [] } = { ...item }
        const hasCombineShipTag = tags.some(item => /^combine-ship(-\w+)?$/.test(item))
        const { labelDetails } = { ...first_barcode_in_batch_ship }
        const { shipping_carrier } = { ...labelDetails }
        const listProduct = this._checkTypeProducts(item.products)
        const disabled = (shipping_carrier === 'SB_FAST' && !hasCombineShipTag) ? false : (listProduct.length === 0 ? !canPrint : (hasCombineShipTag ? !canPrint : (listProduct.length + current < total)))
        const { shipped_at, barcodeNumber, country, state } = item
        const locationType = getOrderLocationType(country, state)

        return (
            <div className="PrintOrderDetails">
                <Card>
                    <CardHeader className="PrintOrderDetailsHeader">
                        <h4 className="d-flex justify-content-between align-items-center w-100 font-weight-bold mb-2">
                            <div className="text-primary">#{barcodeNumber}</div>
                            <div>
                                <Button onClick={this._handleCancel} className="Cancel">
                                    Hủy lệnh đóng gói
                                    <i className="icon-cancel-square ml-2" />
                                </Button>

                            </div>
                        </h4>
                    </CardHeader>
                    <CardBody>
                        <div className="row">
                            <div className="col">
                                {!!shipped_at &&
                                    <h3 style={{ fontSize: '2rem' }}>Shipped at:
                                        {moment(shipped_at).format('HH:mm DD/MM/YYYY')}</h3>
                                }
                                <ItemPackageStatus barcodeNumber={barcodeNumber} />
                                <h3>Địa chỉ: {locationType}</h3>
                            </div>
                            <div className="col">
                                <textarea className="form-control" name="note" value={note}
                                    onChange={this._handleChangeInput} />
                            </div>
                        </div>

                        <div className="text-right mt-2">
                            <Button color="primary" onClick={this._handleSaveNote} disabled={isLoadingNote}>
                                Save Note
                                {this._renderIconNote()}
                            </Button>
                        </div>
                    </CardBody>
                </Card>
                <div className="text-center mb-3">
                    <Button className="ButtonLarge" color="primary" onClick={this._handleClickShip}
                        disabled={isLoadingShipping || disabled}>
                        Đóng gói xong
                        {this._renderIconShip()}
                    </Button>
                    {error.length > 0 && (
                        <div className="mt-3">
                            <div className="text-danger mb-1 errorText"><strong>Lỗi:</strong> Không thể dán label</div>
                            {
                                error.map(_item => {
                                    return <div
                                        className="">- <strong>#{_item.barcodeNumber}_{_item.itemNumber}</strong>: {_item.errorMsg}
                                    </div>
                                })
                            }
                        </div>
                    )}
                </div>
            </div>
        )
    }
}

ShipOrderDetails.propTypes = {
    ableToPrint: PropTypes.func.isRequired,
}

export default ShipOrderDetails
